import React, { useMemo } from 'react'
import { SCREEN_MULTIMEDIA, SCREENS } from '../../../../../../services/domain/wizardScreens'
import { Attachment, AttachmentData, AttachmentType } from '../../../../../../@types/types'
import { MultimediaUploaderType } from '../../../../../../@types/multimedia-types'
import { MultimediaModalsContextProvider } from './multimediaDetailsModalContext/multimediaModalsContext'
import {
  changeFloorPlanStatus,
  deleteAttachment,
  editAttachmentName,
} from '../../../../../../redux/thunks/multimedia/multimedia'
import { useAppDispatch } from '../../../../../../app/hooks'
import { isWebView } from '../../../../../../utils/navigation'
import PictureList from './atachmentsList/picture/PictureList'
import DocumentList from './atachmentsList/document/DocumentList'
import VideoList from './atachmentsList/video/VideoList'
import MultimediaButtons from './multimediaButtons/MultimediaButtons'

const FILE_NAME_VALIDATION_REGEX = /^[A-Za-z0-9_ ÄÖÜäöüß\-]{1,30}$/

const screen = SCREENS[SCREEN_MULTIMEDIA]

type AttachmentsWrapperProps = {
  hasOnlyPictures: boolean
  multimediaContent: AttachmentData[]
  allowedTypesToDisplay: MultimediaUploaderType[]
}

const AttachmentsWrapper: React.FC<AttachmentsWrapperProps> = ({
  hasOnlyPictures,
  multimediaContent,
  allowedTypesToDisplay,
}) => {
  const hasDocumentOrPicture =
    multimediaContent.filter((value) => value.attachmentType !== AttachmentType.LINK).length > 0
  const dispatch = useAppDispatch()
  const isMobile = isWebView()

  const onAttachmentDelete = async (attachment: Attachment) => {
    await dispatch(deleteAttachment(attachment.id, attachment.source, attachment.size))
  }

  const onAttachmentTitleChange = async (changedAttachment: AttachmentData) => {
    const newTitle = changedAttachment.attachment!.title!
    if (FILE_NAME_VALIDATION_REGEX.test(newTitle) && newTitle.trim().length > 0) {
      await dispatch(
        editAttachmentName(
          changedAttachment.attachment!.id,
          newTitle,
          changedAttachment.attachment!.source,
          changedAttachment.file!.size
        )
      )
    }
  }

  const onAttachmentFloorPlanStatusChange = (changedAttachment: AttachmentData) => {
    dispatch(changeFloorPlanStatus(changedAttachment.attachment!.id))
  }

  const modalAttachmentData = useMemo(
    () =>
      multimediaContent.filter(
        (a) => a.attachmentType === AttachmentType.PICTURE || a.attachmentType === AttachmentType.DOCUMENT
      ),
    [multimediaContent]
  )

  return (
    <MultimediaModalsContextProvider
      attachmentsData={modalAttachmentData}
      onAttachmentTitleChange={onAttachmentTitleChange}
      onAttachmentFloorPlanStatusChange={onAttachmentFloorPlanStatusChange}
      onAttachmentDelete={onAttachmentDelete}
    >
      {!isMobile && (
        <div className='grid-item one-whole'>
          <div className='grid gutter-m'>
            {hasDocumentOrPicture && <PictureList attachmentData={modalAttachmentData} />}
            {!hasOnlyPictures && <DocumentList screenId={screen.id} attachmentData={modalAttachmentData} />}
            {hasDocumentOrPicture && <VideoList />}
          </div>
        </div>
      )}
      <div className='grid-item one-whole' data-testid='multimedia-buttons'>
        <div className={isWebView() ? '' : 'grid gutter-m'}>
          <MultimediaButtons hasButtonBigSize={hasOnlyPictures} allowedMediaTypes={allowedTypesToDisplay} />
        </div>
      </div>
    </MultimediaModalsContextProvider>
  )
}

export default AttachmentsWrapper
